// src/pages/About/index.js
import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFaceGrinBeamSweat } from "@fortawesome/free-regular-svg-icons";
import {
    faBriefcase,
    faEarthAsia,
    faUserGraduate,
    faXmark,
    faImages,
    faChevronLeft,
    faChevronRight,
    faPlay,
} from "@fortawesome/free-solid-svg-icons";
import { faYoutube } from "@fortawesome/free-brands-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCards, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-cards";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { motion, AnimatePresence } from "framer-motion";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import ChillImg from "../../assets/imgs/images.jpg";
import GDULogo from "../../assets/imgs/gdu-logo.png";
import Img from "../../components/img";
import useSEO from "../../hooks/useSEO";
import { pageVariants, sectionVariants, containerVariants, itemVariants } from "../../config/animations";

// Gallery dynamic load (Webpack/CRA version)
const importAll = (r) => {
    return r
        .keys()
        .sort((a, b) => {
            const aNum = parseInt(a.match(/\d+/)?.[0] || 0);
            const bNum = parseInt(b.match(/\d+/)?.[0] || 0);
            return aNum - bNum;
        })
        .map((item) => {
            const name = item.match(/\d+/)?.[0] || "Image";
            return {
                src: r(item),
                name: `Images ${name}`,
            };
        });
};

const certImages = importAll(require.context("../../assets/gallery", false, /\.(png|jpe?g|svg|webp)$/));

function About() {
    useSEO({
        title: "About",
        description: "Tìm hiểu về hành trình công việc, nền tảng giáo dục và kinh nghiệm làm việc của tôi. Khám khám phá các kỹ năng và thành tựu của tôi.",
        keywords: "about, education, experience, skills, developer, SoulLab_, hienleeone",
        url: "https://owner.soulmc.vn/"
    });

    const [preview, setPreview] = useState(null);
    const [activeCert, setActiveCert] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [videoLoading, setVideoLoading] = useState(true);
    const activeIndex = certImages.findIndex(c => c.name === activeCert);
    const galleryProgressPercent = activeIndex >= 0 ? ((activeIndex + 1) / certImages.length) * 100 : 0;

    const currentPreviewIndex = preview ? certImages.findIndex(c => c.name === preview.name) : -1;

    const goNextPreview = useCallback(() => {
        if (currentPreviewIndex !== -1 && certImages.length > 0) {
            const nextIdx = (currentPreviewIndex + 1) % certImages.length;
            setPreview(certImages[nextIdx]);
        }
    }, [currentPreviewIndex]);

    const goPrevPreview = useCallback(() => {
        if (currentPreviewIndex !== -1 && certImages.length > 0) {
            const prevIdx = (currentPreviewIndex - 1 + certImages.length) % certImages.length;
            setPreview(certImages[prevIdx]);
        }
    }, [currentPreviewIndex]);

    // Lock body scroll when any modal is open & cleanup on unmount
    useEffect(() => {
        if (preview || videoPreview) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [preview, videoPreview]);

    // Keyboard navigation (Escape, ArrowLeft, ArrowRight)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (preview) {
                if (e.key === "Escape") setPreview(null);
                if (e.key === "ArrowRight") goNextPreview();
                if (e.key === "ArrowLeft") goPrevPreview();
            } else if (videoPreview) {
                if (e.key === "Escape") {
                    setVideoPreview(null);
                    setVideoLoading(true);
                }
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [preview, videoPreview, goNextPreview, goPrevPreview]);

    const favoriteVideos = [
        {
            type: "youtube",
            id: "https://www.youtube.com/embed/k8m0SaGQ_1c",
            title: "Welcome to VietNam",
            thumbnail: "https://img.youtube.com/vi/k8m0SaGQ_1c/maxresdefault.jpg",
        },
        {
            type: "youtube",
            id: "https://www.youtube.com/embed/BsNm0Zv6hnE",
            title: "Nắng Lung Linh",
            thumbnail: "https://img.youtube.com/vi/BsNm0Zv6hnE/maxresdefault.jpg",
        },
        {
            type: "youtube",
            id: "https://www.youtube.com/embed/BU7WQbSPqk0",
            title: "Lạc Quan Lên!",
            thumbnail: "https://img.youtube.com/vi/BU7WQbSPqk0/maxresdefault.jpg",
        },
        {
            type: "youtube",
            id: "https://www.youtube.com/embed/4m7TFnwR7vI",
            title: "Chill Night Ride",
            thumbnail: "https://img.youtube.com/vi/4m7TFnwR7vI/maxresdefault.jpg",
        },
        {
            type: "youtube",
            id: "https://www.youtube.com/embed/2FE7pOBbRn8",
            title: "Việt Nam Trong Minecraft",
            thumbnail: "https://img.youtube.com/vi/2FE7pOBbRn8/maxresdefault.jpg",
        },
        {
            type: "youtube",
            id: "https://www.youtube.com/embed/oBokMzyNz8o",
            title: "Minecraft Chill",
            thumbnail: "https://img.youtube.com/vi/oBokMzyNz8o/maxresdefault.jpg",
        },
    ];


    return (
        <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={pageVariants}
            className="w-full"
        >
            {/* Main Glass Content Wrapper */}
            <div
                className="flex p-3 sm:p-4 rounded-xl bg-white/40 dark:bg-slate-900/60 backdrop-blur-xl border border-white/20 dark:border-slate-800/50 shadow-xl transition-colors duration-300 overflow-hidden"
            >
                <div className="text-neutral-800 dark:text-neutral-200 w-full pb-4 font-normal">
                    {/* Title */}
                    <motion.div className="mb-5" variants={itemVariants}>
                        <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/60 dark:bg-slate-900/60 border border-white/20 dark:border-slate-800/50 rounded-xl shadow-sm hover:shadow-md transition-all cursor-default group">
                            <div className="w-1.5 h-6 bg-slate-800 dark:bg-slate-200 rounded-full group-hover:scale-y-110 transition-transform" />
                            <h2 className="text-xl md:text-2xl font-bold">Giới thiệu về tôi 💤</h2>
                        </div>
                    </motion.div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-5 lg:gap-6">
                        {/* Left */}
                        <div className="md:col-span-3 space-y-5">
                            <motion.section variants={sectionVariants}>
                                <h3 className="mb-2.5 text-lg font-bold flex items-center gap-2.5">
                                    <span className="text-neutral-400 dark:text-neutral-600 text-xs">⬤</span>
                                    Tôi đang làm gì? 🤔
                                </h3>
                                <div className="text-slate-700 dark:text-slate-300 leading-relaxed pl-5 font-normal text-xs sm:text-sm space-y-2.5">
                                    <p>
                                        Sau một thời gian thử sức ở các lĩnh vực khác nhau, hiện tại tôi đang tập trung quay trở lại và hướng tới chuyên ngành công nghệ đã học. Công việc hiện nay giúp tôi tiếp cận sâu hơn về hệ thống và mạng máy tính, củng cố lại các kiến thức cơ bản và tích lũy thêm kinh nghiệm thực tế.
                                    </p>
                                    <p>
                                        Bên cạnh giờ làm việc, tôi dành thời gian tự học lập trình và tham gia phát triển các dự án game cộng đồng. Định hướng của tôi là kết hợp kiến thức bảo mật sẵn có nhằm góp phần xây dựng những sân chơi giải trí ổn định, an toàn và tối ưu trải nghiệm người dùng.
                                    </p>
                                </div>
                            </motion.section>

                            <motion.section variants={sectionVariants}>
                                <h3 className="mb-2.5 text-lg font-bold flex items-center gap-2.5">
                                    <span className="text-neutral-400 dark:text-neutral-600 text-xs">⬤</span>
                                    Web này là gì? 🍜
                                </h3>
                                <div className="flex flex-col lg:flex-row gap-5 items-start pl-5">
                                    <div className="flex-1 order-2 lg:order-1">
                                        <div className="text-slate-700 dark:text-slate-300 leading-relaxed font-normal text-xs sm:text-sm space-y-2.5">
                                            <p>
                                                Trang web này là một không gian nhỏ cá nhân để tôi lưu giữ hành trình học hỏi, tổng hợp một số dự án thực tế và chia sẻ các kiến thức công nghệ mà mình tích lũy được trong suốt quá trình làm việc cũng như tự học.
                                            </p>
                                            <p>
                                                Đồng thời, đây cũng là chiếc cầu nối mở giúp tôi giao lưu, kết nối với những bạn có chung niềm đam mê. Tôi luôn sẵn sàng chào đón các ý kiến chia sẻ kỹ thuật mang tính xây dựng và những cơ hội hợp tác thú vị để cùng học hỏi và phát triển.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0 order-1 lg:order-2 self-center lg:self-start">
                                        <motion.div
                                            whileHover={{ scale: 1.05 }}
                                            className="relative overflow-hidden rounded-2xl border-4 border-white dark:border-slate-800 shadow-xl"
                                        >
                                            <Img
                                                className="select-none w-32 md:w-36 lg:w-44 transition-transform duration-700 hover:scale-110"
                                                alt="img"
                                                src={ChillImg}
                                            />
                                        </motion.div>
                                    </div>
                                </div>
                            </motion.section>

                            <motion.section variants={sectionVariants}>
                                <h3 className="mb-3 text-lg font-bold flex items-center gap-2.5">
                                    <span className="text-neutral-400 dark:text-neutral-600 text-xs">⬤</span>
                                    Video yêu thích của tôi
                                    <FontAwesomeIcon icon={faYoutube} className="text-slate-700 dark:text-slate-300 text-base" />
                                </h3>

                                <div className="relative px-4">
                                    <Swiper
                                        spaceBetween={16}
                                        slidesPerView={1}
                                        loop={true}
                                        navigation={{
                                            nextEl: '.video-swiper-next',
                                            prevEl: '.video-swiper-prev',
                                        }}
                                        modules={[Navigation]}
                                        className="rounded-xl"
                                    >
                                        {favoriteVideos.map((video, i) => (
                                            <SwiperSlide key={i}>
                                                <motion.div
                                                    whileHover={{ scale: 1.01 }}
                                                    className="p-3.5 sm:p-4 rounded-xl border bg-white/40 dark:bg-slate-900/60 border-white/20 dark:border-slate-800/50 shadow-lg hover:shadow-xl transition cursor-pointer"
                                                    onClick={() => setVideoPreview(video)}
                                                >
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <div className="w-8 h-8 rounded-full bg-slate-800 dark:bg-slate-700 flex items-center justify-center">
                                                            <FontAwesomeIcon icon={faYoutube} className="text-white text-xs" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="font-bold text-slate-900 dark:text-white text-xs sm:text-sm">{video.title}</p>
                                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">YouTube Video</p>
                                                        </div>
                                                    </div>
                                                    <div className="relative aspect-video bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden flex items-center justify-center group">
                                                        <img
                                                            src={video.thumbnail}
                                                            alt={video.title}
                                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                            loading="lazy"
                                                            decoding="async"
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                        <div className="relative z-10 w-11 h-11 rounded-full bg-slate-800/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                                            <FontAwesomeIcon icon={faPlay} className="text-white text-base ml-0.5" />
                                                        </div>
                                                    </div>
                                                    <p className="text-center text-slate-500 dark:text-slate-400 text-[11px] mt-2.5">Nhấn để phát video</p>
                                                </motion.div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                    <button className="video-swiper-prev absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-slate-800/60 dark:bg-slate-700/60 text-white flex items-center justify-center hover:bg-slate-800 dark:hover:bg-slate-600 transition z-10 shadow-lg backdrop-blur-sm border border-white/20 text-xs">
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                    <button className="video-swiper-next absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-slate-800/60 dark:bg-slate-700/60 text-white flex items-center justify-center hover:bg-slate-800 dark:hover:bg-slate-600 transition z-10 shadow-lg backdrop-blur-sm border border-white/20 text-xs">
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 text-center pl-5 font-normal">
                                    Nhấn vào thẻ để phóng to video
                                </p>
                            </motion.section>
                        </div>

                        {/* Right */}
                        <motion.div className="md:col-span-2 flex flex-col gap-4" variants={containerVariants}>
                            <motion.div
                                className="p-3.5 rounded-xl border bg-white/40 dark:bg-slate-900/60 border-white/20 dark:border-slate-800/50 shadow-md hover:shadow-xl transition transform"
                                variants={itemVariants}
                            >
                                <div className="flex gap-2.5 items-center text-slate-700 dark:text-slate-200 font-semibold text-sm">
                                    <FontAwesomeIcon icon={faBriefcase} />
                                    <p>Công việc</p>
                                </div>
                                <div className="flex mt-4 gap-3 items-center">
                                    <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-base flex items-center justify-center shadow-md">
                                        <FontAwesomeIcon icon={faEarthAsia} />
                                    </div>
                                    <div className="flex-1">
                                        <h5 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white">Freelancer</h5>
                                        <div className="flex justify-between text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                                            <p>Dịch vụ - Kinh doanh</p>
                                            <p>2023 - Hiện tại</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div
                                className="p-3.5 rounded-xl border bg-white/40 dark:bg-slate-900/60 border-white/20 dark:border-slate-800/50 shadow-md hover:shadow-xl transition transform"
                                variants={itemVariants}
                            >
                                <div className="flex gap-2.5 items-center text-slate-700 dark:text-slate-200 font-semibold text-sm">
                                    <FontAwesomeIcon icon={faUserGraduate} />
                                    <p>Học vấn</p>
                                </div>
                                <div className="flex mt-4 gap-3 items-start">
                                    <Tippy content="GDU - Đại học Gia Định">
                                        <motion.div
                                            whileHover={{ scale: 1.1 }}
                                            className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border shadow cursor-pointer p-1 flex-shrink-0"
                                        >
                                            <img src={GDULogo} alt="GDU" className="w-full h-full object-contain" />
                                        </motion.div>
                                    </Tippy>
                                    <div className="flex-1">
                                        <h4 className="text-lg md:text-xl font-bold dark:text-white leading-tight">ĐẠI HỌC GIA ĐỊNH</h4>
                                        <p className="text-xs font-medium text-indigo-500 dark:text-indigo-400">GDU (Gia Dinh University)</p>
                                        <div className="mt-1.5 text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-normal">
                                            <p>Tốt nghiệp chuyên ngành An toàn thông tin tại Đại học Gia Định năm 2024. Nền tảng học thuật giúp tôi nắm vững kiến thức về bảo mật hệ thống, mã hóa dữ liệu và phát triển các giải pháp phòng ngừa rủi ro mạng trong thực tế.</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <a
                                        href="https://giadinh.edu.vn/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full py-2 flex text-xs sm:text-sm font-bold items-center gap-2 justify-center transition-all duration-300 rounded-lg bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 hover:border-slate-400 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-100 dark:border-slate-700 shadow-sm"
                                    >
                                        <FontAwesomeIcon icon={faFaceGrinBeamSweat} />
                                        <span>Website Trường</span>
                                    </a>
                                </div>
                            </motion.div>

                            <motion.div
                                className="p-3.5 rounded-xl border bg-white/40 dark:bg-slate-900/60 border-white/20 dark:border-slate-800/50 shadow-md hover:shadow-xl transition"
                                variants={itemVariants}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-sm">
                                            <FontAwesomeIcon icon={faImages} className="text-slate-700 dark:text-slate-300 text-xs" />
                                        </div>
                                        <span className="font-semibold text-neutral-700 dark:text-neutral-200 text-xs sm:text-sm">Thư viện ảnh</span>
                                    </div>
                                    <div className="px-2.5 py-0.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 text-[11px] font-semibold rounded-full shadow-sm">
                                        {certImages.findIndex(c => c.name === activeCert) + 1} / {certImages.length}
                                    </div>
                                </div>
                                <p className="text-center mb-3 text-xs font-normal">
                                    <span className="text-slate-500 dark:text-slate-400">Đang xem:</span>{' '}
                                    <span className="font-semibold text-slate-700 dark:text-neutral-200">{activeCert}</span>
                                </p>
                                <Swiper
                                    effect="cards"
                                    grabCursor={true}
                                    loop={true}
                                    navigation={{ nextEl: ".cert-swiper-next", prevEl: ".cert-swiper-prev" }}
                                    modules={[EffectCards, Navigation]}
                                    className="w-full max-w-[220px] sm:max-w-[230px] md:max-w-[200px] lg:max-w-[220px] mx-auto"
                                    onSlideChange={(swiper) => {
                                        const realIndex = swiper.realIndex;
                                        if (certImages[realIndex]) {
                                            setActiveCert(certImages[realIndex].name);
                                        }
                                    }}
                                    onInit={(swiper) => {
                                        const realIndex = swiper.realIndex;
                                        if (certImages[realIndex]) {
                                            setActiveCert(certImages[realIndex].name);
                                        }
                                    }}
                                >
                                    {certImages.map((cert, i) => (
                                        <SwiperSlide
                                            key={i}
                                            className="rounded-xl cursor-pointer flex items-center justify-center bg-transparent"
                                            onClick={() => setPreview(cert)}
                                        >
                                            <motion.div
                                                whileHover={{ scale: 1.02 }}
                                                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                                                className="relative w-full overflow-hidden rounded-xl shadow-md border border-slate-200/50 dark:border-slate-800/50 group"
                                            >
                                                <Img src={cert.src} alt={cert.name} className="w-full h-[215px] md:h-[200px] lg:h-[215px] object-cover object-center transition-transform duration-500 group-hover:scale-105" />
                                                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                                    <div className="text-center text-white">
                                                        <FontAwesomeIcon icon={faImages} className="text-xl mb-1" />
                                                        <p className="text-[11px] font-medium">Nhấn để phóng to</p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                <div className="mt-3 flex items-center justify-center gap-3 text-slate-500 dark:text-slate-400 text-xs">
                                    <button className="cert-swiper-prev flex items-center gap-1 hover:text-slate-800 dark:hover:text-white transition-colors cursor-pointer disabled:opacity-30">
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                        <span>Trước</span>
                                    </button>
                                    <div className="w-16 md:w-24 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mt-0.5">
                                        <motion.div
                                            className="h-full bg-slate-700 dark:bg-slate-300"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${galleryProgressPercent}%` }}
                                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                        />
                                    </div>
                                    <button className="cert-swiper-next flex items-center gap-1 hover:text-slate-800 dark:hover:text-white transition-colors cursor-pointer disabled:opacity-30">
                                        <span>Sau</span>
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Modals outside the Glass wrapper (Portaled to body to avoid stacking context overlap) */}
            {typeof document !== "undefined" && createPortal(
                <AnimatePresence>
                    {videoPreview && (
                        <motion.div
                            className="fixed inset-0 bg-slate-900/50 dark:bg-black/90 backdrop-blur-xl flex items-center justify-center z-[99999] p-4 sm:p-6"
                            style={{ zIndex: 999999 }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => { setVideoPreview(null); setVideoLoading(true); }}
                        >
                            <motion.div
                                className="relative w-full max-w-4xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden shadow-2xl flex flex-col"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="px-4 py-3 sm:px-5 sm:py-3.5 flex items-center justify-between border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-900/50 backdrop-blur-md">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-white text-[10px] font-bold uppercase tracking-wider transition-colors">YouTube</span>
                                            <span className="text-slate-400 dark:text-white/40 text-[10px] uppercase font-bold tracking-widest transition-colors">Video Stream</span>
                                        </div>
                                        <h3 className="text-slate-900 dark:text-white font-bold truncate text-base sm:text-lg leading-tight transition-colors">{videoPreview.title}</h3>
                                    </div>
                                    <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-white hover:bg-slate-100 text-slate-700 hover:text-slate-900 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 shadow-sm transition-all active:scale-90" onClick={() => { setVideoPreview(null); setVideoLoading(true); }}>
                                        <FontAwesomeIcon icon={faXmark} className="text-sm" />
                                    </button>
                                </div>
                                <div className="w-full bg-black relative aspect-video" style={{ height: "auto", maxHeight: "calc(100vh - 160px)" }}>
                                    {videoLoading && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 gap-4">
                                            <div className="w-10 h-10 border-2 border-slate-200 dark:border-slate-700 border-t-indigo-500 dark:border-t-white rounded-full animate-spin"></div>
                                            <p className="text-slate-400 dark:text-white/30 text-[10px] font-bold uppercase tracking-widest transition-colors">Loading...</p>
                                        </div>
                                    )}
                                    <iframe
                                        className={`absolute top-0 left-0 w-full h-full transition-opacity duration-1000 ${videoLoading ? 'opacity-0' : 'opacity-100'}`}
                                        src={videoPreview.id + "?autoplay=1&rel=0&showinfo=0&mute=0&origin=" + window.location.origin}
                                        title={videoPreview.title}
                                        allow="autoplay; fullscreen; clipboard-write; encrypted-media; picture-in-picture"
                                        allowFullScreen
                                        onLoad={() => setVideoLoading(false)}
                                        style={{ border: "none" }}
                                    ></iframe>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}

            {typeof document !== "undefined" && createPortal(
                <AnimatePresence>
                    {preview && (
                        <motion.div
                            className="fixed inset-0 bg-slate-900/50 dark:bg-black/90 backdrop-blur-xl flex items-center justify-center z-[99999] p-4 sm:p-8"
                            style={{ zIndex: 999999 }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setPreview(null)}
                        >
                            {/* Desktop Floating Arrows */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goPrevPreview();
                                }}
                                className="hidden sm:flex absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-[110] w-12 h-12 items-center justify-center rounded-full bg-white/80 hover:bg-white text-slate-700 hover:text-slate-900 border border-slate-300/80 shadow-xl backdrop-blur-md dark:bg-slate-800/80 dark:hover:bg-slate-750 dark:text-white dark:border-slate-700 text-lg transition-all active:scale-95 cursor-pointer"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goNextPreview();
                                }}
                                className="hidden sm:flex absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-[110] w-12 h-12 items-center justify-center rounded-full bg-white/80 hover:bg-white text-slate-700 hover:text-slate-900 border border-slate-300/80 shadow-xl backdrop-blur-md dark:bg-slate-800/80 dark:hover:bg-slate-750 dark:text-white dark:border-slate-700 text-lg transition-all active:scale-95 cursor-pointer"
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>

                            <motion.div
                                className="relative max-w-3xl w-full flex flex-col gap-3"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex justify-between items-center text-slate-800 dark:text-white px-2">
                                    <div className="flex flex-col">
                                        <p className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-0.5 transition-colors">Xem trước</p>
                                        <p className="font-bold text-lg transition-colors"><span className="text-blue-500 dark:text-blue-400">{preview.name}</span></p>
                                    </div>
                                    <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-white hover:bg-slate-100 text-slate-700 hover:text-slate-900 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 shadow-sm transition-all active:scale-95" onClick={() => setPreview(null)}>
                                        <FontAwesomeIcon icon={faXmark} className="text-sm" />
                                    </button>
                                </div>
                                <div className="relative group overflow-hidden rounded-xl border border-slate-200 dark:border-white/10 shadow-2xl bg-slate-100/70 dark:bg-neutral-900">
                                    <Img src={preview.src} alt={preview.name} className="w-full h-auto max-h-[70vh] sm:max-h-[75vh] object-contain mx-auto" />
                                </div>
                                <div className="flex justify-center items-center gap-3">
                                    <button
                                        onClick={goPrevPreview}
                                        className="sm:hidden px-3 py-1.5 bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 text-slate-700 dark:text-white border border-slate-300/80 dark:border-slate-700 backdrop-blur-md rounded-lg text-xs font-bold flex items-center gap-1 active:scale-95 shadow-md"
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} /> Trước
                                    </button>
                                    <div className="px-5 py-1.5 bg-white/80 dark:bg-slate-800/80 border border-slate-300/80 dark:border-slate-700 text-slate-700 dark:text-white text-xs sm:text-sm font-bold shadow-sm backdrop-blur-md rounded-full">
                                        {currentPreviewIndex + 1} / {certImages.length}
                                    </div>
                                    <button
                                        onClick={goNextPreview}
                                        className="sm:hidden px-3 py-1.5 bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 text-slate-700 dark:text-white border border-slate-300/80 dark:border-slate-700 backdrop-blur-md rounded-lg text-xs font-bold flex items-center gap-1 active:scale-95 shadow-md"
                                    >
                                        Sau <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </motion.div>
    );
}

export default About;